import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchCollectionsReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const CollectionsReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const {
    data: collectionsData,
    loading,
    error,
  } = useSelector((state) => state.reports.collections || { data: null, loading: false, error: null });

  useEffect(() => {
    dispatch(fetchCollectionsReport({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const collections = collectionsData?.data?.collections || [];
  const summary = collectionsData?.data?.summary || {};

  const columns = [
    {
      header: "Invoice ID",
      accessor: "invoiceId",
    },
    {
      header: "Client Name",
      accessor: "clientName",
    },
    {
      header: "Client Email",
      accessor: "clientEmail",
    },
    {
      header: "Clinician",
      accessor: "clinicianName",
    },
    {
      header: "Clinic",
      accessor: "clinicName",
    },
    {
      header: "Service Date",
      accessor: (row) => format(new Date(row.serviceDate), "MM/dd/yyyy"),
    },
    {
      header: "Invoice Amount",
      accessor: (row) => `$${row.invoiceAmount.toFixed(2)}`,
    },
    {
      header: "Total Paid",
      accessor: (row) => `$${row.totalPaid.toFixed(2)}`,
    },
    {
      header: "Overdue Amount",
      accessor: (row) => `$${row.overdueAmount.toFixed(2)}`,
      cell: (row) => (
        <span className={row.overdueAmount > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
          ${row.overdueAmount.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: (row) => row.overdueAmount <= 0 ? "Paid" : "Overdue",
      cell: (row) => {
        const isPaid = row.overdueAmount <= 0;
        const status = isPaid ? "Paid" : "Overdue";
        const className = isPaid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
        
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${className}`}>
            {status}
          </span>
        );
      },
    },
    {
      header: "Days Overdue",
      accessor: (row) => row.isOverdue ? row.daysOverdue : "-",
      cell: (row) => (
        <span className={row.isOverdue ? "text-red-600 font-semibold" : ""}>
          {row.isOverdue ? row.daysOverdue : "-"}
        </span>
      ),
    },
    {
      header: "Bill To",
      accessor: "billToType",
    },
  ];

  const handleDateChange = (field, value) => {
    if (field === "startDate") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getDownloadLabel = () => {
    return "Download Collections Report - Overdue Invoices";
  };

  const handleDownload = () => {
    if (!collections || collections.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    // Collections data
    const collectionsData = collections.map((item) => ({
      "Invoice ID": item.invoiceId,
      "Client Name": item.clientName,
      "Client Email": item.clientEmail,
      "Clinician": item.clinicianName,
      "Clinic": item.clinicName,
      "Service Date": format(new Date(item.serviceDate), "MM/dd/yyyy"),
      "Invoice Amount": item.invoiceAmount.toFixed(2),
      "Total Paid": item.totalPaid.toFixed(2),
      "Overdue Amount": item.overdueAmount.toFixed(2),
      "Status": item.overdueAmount <= 0 ? "Paid" : "Overdue",
      "Days Overdue": item.isOverdue ? item.daysOverdue : "-",
      "Bill To": item.billToType,
      "Notes": item.notes,
    }));

    // Summary data including deposits
    const summaryData = [
      { "Metric": "Total Invoiced", "Amount": summary.totalInvoiced?.toFixed(2) || "0.00" },
      { "Metric": "Total Collected", "Amount": summary.totalCollected?.toFixed(2) || "0.00" },
      { "Metric": "Total Overdue", "Amount": summary.totalOverdue?.toFixed(2) || "0.00" },
      { "Metric": "Total Invoices", "Amount": summary.totalInvoices || 0 },
      { "Metric": "Overdue Invoices", "Amount": summary.overdueInvoices || 0 },
      { "Metric": "Overdue Amount", "Amount": summary.overdueAmount?.toFixed(2) || "0.00" },
      { "Metric": "Total Deposits Collected", "Amount": summary.totalDepositsCollected?.toFixed(2) || "0.00" },
      { "Metric": "Total Deposits Used", "Amount": summary.totalDepositsUsed?.toFixed(2) || "0.00" },
      { "Metric": "Net Deposit Balance", "Amount": summary.netDepositBalance?.toFixed(2) || "0.00" },
    ];

    const workbook = XLSX.utils.book_new();
    
    // Add collections sheet
    const collectionsWorksheet = XLSX.utils.json_to_sheet(collectionsData);
    XLSX.utils.book_append_sheet(workbook, collectionsWorksheet, "Collections Report");
    
    // Add summary sheet
    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

    const fileName = `Collections_Report_Overdue_${startDate}_to_${endDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  if (loading) return <div className="flex justify-center items-center h-64">Loading...</div>;
  if (error) return <div className="text-red-500 p-4">{error}</div>;

  // Calculate pagination
  const totalItems = collections.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = collections.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange("startDate", e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => handleDateChange("endDate", e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>
        <div className="mt-3">
          <p className="text-sm text-gray-600">
            <strong>Note:</strong> This report only shows overdue invoices, sorted by most days overdue first.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Invoiced</h4>
          <p className="text-2xl font-bold text-gray-900">${summary.totalInvoiced?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Collected</h4>
          <p className="text-2xl font-bold text-green-600">${summary.totalCollected?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Overdue</h4>
          <p className="text-2xl font-bold text-red-600">${summary.totalOverdue?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Overdue Amount</h4>
          <p className="text-2xl font-bold text-red-600">${summary.overdueAmount?.toFixed(2) || "0.00"}</p>
        </div>
      </div>

      {/* Additional Summary Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Invoices</h4>
          <p className="text-xl font-semibold text-gray-900">{summary.totalInvoices || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Overdue Invoices</h4>
          <p className="text-xl font-semibold text-red-600">{summary.overdueInvoices || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Deposits Collected</h4>
          <p className="text-xl font-semibold text-blue-600">${summary.totalDepositsCollected?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Deposits Used</h4>
          <p className="text-xl font-semibold text-orange-600">${summary.totalDepositsUsed?.toFixed(2) || "0.00"}</p>
        </div>
      </div>

      {/* Deposit Information */}
      <div className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Deposit Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Deposits Collected</h4>
            <p className="text-3xl font-bold text-blue-600">${summary.totalDepositsCollected?.toFixed(2) || "0.00"}</p>
            <p className="text-sm text-gray-600">Amount of deposits received during this period</p>
          </div>
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Deposits Used</h4>
            <p className="text-3xl font-bold text-orange-600">${summary.totalDepositsUsed?.toFixed(2) || "0.00"}</p>
            <p className="text-sm text-gray-600">Amount of deposits applied to invoices during this period</p>
          </div>
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Net Deposit Balance</h4>
            <p className={`text-3xl font-bold ${(summary.netDepositBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${summary.netDepositBalance?.toFixed(2) || "0.00"}
            </p>
            <p className="text-sm text-gray-600">
              {(summary.netDepositBalance || 0) >= 0 ? 
                "Deposits collected exceed deposits used" : 
                "Deposits used exceed deposits collected"}
            </p>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white shadow-sm rounded-sm">
        <Table
          columns={columns}
          data={paginatedData}
          title="Collections Report - Overdue Invoices"
          description="Overdue invoices sorted by most days overdue first - focus on urgent collections"
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalItems}
          onPageChange={handlePageChange}
          actions={[
            {
              label: getDownloadLabel(),
              onClick: handleDownload,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CollectionsReport;
